import { Trans } from 'react-i18next';
import { Header, Label } from 'semantic-ui-react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';
import React from 'react';
import $ from 'jquery';
import DataTable from 'datatables.net';
import fixedColumns from 'datatables.net-fixedcolumns'; // eslint-disable-line no-unused-vars
import { urlPrefix } from './quotaSummary';
import useAppContext from '../../context/useAppContext';
import Loading from '../../components/loading';
import { WeezHeader } from '@weezevent/nacre';

$.DataTable = DataTable;

function QuotaStats({ booked, booking, total }) {
    return (
        <React.Fragment>
            <span className="tabular-text-booking" key="tabular-text-booking">
                {booked}
            </span>
            {booking ? `(+${booking})` : null}
            {` / ${total}`}
        </React.Fragment>
    );
}
QuotaStats.propTypes = {
    booked: PropTypes.string.isRequired,
    booking: PropTypes.bool.isRequired,
    total: PropTypes.number.isRequired
};

function ZoneCapacity({ zone }) {
    if (!zone.seatCount()) {
        return null;
    }

    return (
        <span className="tabular-zone-capacity">
            <Trans
                i18nKey="tabularZoneCapacity"
                values={{
                    capacity: zone.seatCount()
                }}
            >
                Capacity {zone.seatCount()}
            </Trans>
        </span>
    );
}
ZoneCapacity.propTypes = {
    zone: PropTypes.object.isRequired
};

function Quota({ quota }) {
    if (!quota) {
        // can be created
        return <td className="tabular-quota-creation" key={`tabular-quota-creation-${Math.random()}`}></td>;
    }
    return (
        <td className="tabular-quota-quota" key={`tabular-quota-${quota.id}`}>
            <QuotaStats booked={quota.booked} booking={quota.bookingInProgress} total={quota.total} />
        </td>
    );
}
Quota.propTypes = {
    quota: PropTypes.object.isRequired
};

function Headers({ zones }) {
    let headers = [
        <th key="baseRatesTitle" className="tabular-quota-header">
            <Trans i18nKey="ratesZonesTitle">
                <span>Rates / Zones</span>
            </Trans>
        </th>
    ];

    zones.forEach(zone => {
        headers.push(
            <th className="tabular-quota-header" key={`tabular-quota-header-zone-${zone.pk}`}>
                {zone.name}
                <br />
                <ZoneCapacity zone={zone} />
            </th>
        );
    });

    headers.push(
        <th className="tabular-quota-header" key="tabular-quota-header-total">
            <Trans i18nKey="totalWord">
                <span>Total</span>
            </Trans>
        </th>
    );

    headers.push(
        <th className="tabular-quota-header" key="tabular-quota-header-modify">
            <Trans i18nKey="ticket.common.action.edit">
                <span>Modify</span>
            </Trans>
        </th>
    );

    return headers;
}

export default function QuotasTabular({ rates, zones, currentInventory, renderInventorySelector, contingent, history, quotas }) {
    const [{ event, organization }] = useAppContext();
    const [currentTable, setCurrentTable] = React.useState(null);
    let organizationId = organization.id;
    let eventId = event.id;

    React.useEffect(() => {
        if (currentTable) {
            currentTable.destroy();
        }

        if (quotas) {
            datatable();
        }
    }, [quotas, rates, zones, currentInventory, contingent]);

    // Unmount handler to destroy datatable
    React.useEffect(() => {
        return () => {
            if (currentTable) {
                currentTable.destroy();
            }
        };
    }, [currentTable]);

    /**
     * Renders a disabled td (unable because user is unable to add a quota)
     * @returns {React.Component} Disabled TD
     */
    const renderUnable = React.useCallback((prates, zone) => {
        let ratesId = prates.map(rate => rate.id).join(',');
        return <td key={`tabular-quota-disabled-${ratesId}-${zone.pk}`} className="tabular-quota-disabled"></td>;
    }, []);

    /**
     * Render a rates list separated by carriage return
     * @param rates
     * @returns {string} Rates names
     */
    const getRateName = React.useCallback(prates => {
        return prates.map(rate => rate.resolved_name).join('\n-\n');
    }, []);

    const getQuotaRates = React.useCallback(
        prates => {
            return rates.filter(rate => prates.indexOf(rate.id) > -1);
        },
        [rates]
    );

    /**
     * Generate a modify button to link to the quota viewer for this GA
     * @param {Rate[]} rates - Rates to which this quota applies
     */
    const getModifyButton = React.useCallback(
        (contingent, rates) => {
            let selectedRates = rates.map(rate => rate.id).join(',');
            return (
                <td className="tabular-quota-total">
                    <Link
                        to={`${urlPrefix(eventId, organizationId)}/seating/quotas/inventory/${currentInventory.id()}/contingent/${
                            contingent.pk
                        }/edit?selectedRates=${selectedRates}&editMode=true`}
                    >
                        <Trans i18nKey="ticket.common.action.edit">Modify</Trans>
                    </Link>
                </td>
            );
        },
        [rates]
    );

    /**
     * Generate rows for the matrix
     * @returns {Array[]} Array fo arrays (matrix)
     */
    const getRows = React.useCallback(() => {
        let rows = [];
        let quotasByRateKey = {};
        // get all rates with groups
        quotas.forEach(quota => {
            const key = quota.rateKey;
            if (!quotasByRateKey[key]) {
                let quotaRates = getQuotaRates(quota.rates);
                quotasByRateKey[key] = {
                    name: getRateName(quotaRates),
                    arrayRates: quotaRates
                };
            }
            quotasByRateKey[key][quota.zone] = quota;
        });
        // get all rates that are not in previous table
        rates
            .filter(rate => rate.contingents.indexOf(contingent.pk) > -1)
            .forEach(rate => {
                if (!quotasByRateKey[rate.id]) {
                    quotasByRateKey[rate.id] = {
                        name: rate.resolved_name,
                        arrayRates: [rate]
                    };
                }
            });
        Object.keys(quotasByRateKey).forEach(rateKey => {
            let quota = quotasByRateKey[rateKey];
            let name = quota.name;
            let arrayRates = quota.arrayRates;
            let currentRow = [
                <td key={rateKey} className="tabular-quota-rate-name">
                    {name}
                </td>
            ];

            let booked = 0,
                booking = 0,
                total = 0;

            zones.forEach(zone => {
                // get all categories from rates
                let rateCategories = arrayRates.reduce((categories, rate) => {
                    categories.add(rate.category);
                    return categories;
                }, new Set());

                // check if categories are compatible
                let compatibleCategoriesCount = zone.categories(currentInventory.id()).reduce((count, category) => count + rateCategories.has(category), 0);

                if (compatibleCategoriesCount === 0) {
                    return currentRow.push(renderUnable(arrayRates, zone));
                }

                let quotaForZone = quota[zone.pk];

                if (quotaForZone) {
                    booked += quotaForZone.booked;
                    booking += quotaForZone.bookingInProgress;
                    total += quotaForZone.total;
                }
                currentRow.push(<Quota quota={quotaForZone} />);
            });

            currentRow.push(
                <td className="tabular-quota-total" key="tabular-quota-total">
                    <QuotaStats booked={booked} booking={booking} total={total} />
                </td>
            );
            currentRow.push(getModifyButton(contingent, arrayRates));
            rows.push(currentRow);
        });

        return rows;
    }, [quotas, rates, zones, getModifyButton, getQuotaRates, contingent, renderUnable]);

    /**
     * Renders Matrix Rows
     * @returns {React.Component[]} Rows
     */
    const renderRows = React.useCallback(() => {
        let rows = getRows();
        return rows.map((row, index) => <tr key={`tabularRow-${index}`}>{row}</tr>);
    }, [getRows]);

    const datatable = React.useCallback(() => {
        let tmpCurrentTable = $('#quotas-tabular').DataTable({
            scrollX: true,
            orderFixed: [0, 'asc'],
            paging: false,
            searching: false,
            fixedColumns: {
                leftColumns: 1,
                rightColumns: 2
            }
        });

        // XXX: find a solution for this hack
        setTimeout(() => {
            $('.tabular-quota-header.sorting_asc').click();
        }, 100);

        setCurrentTable(tmpCurrentTable);
    }, [setCurrentTable]);

    if (!quotas) {
        return <Loading />;
    }

    const prefix = urlPrefix(eventId, organizationId);

    return (
        <React.Fragment>
            <div className="header">
                <WeezHeader
                    title={
                        <>
                            <Trans i18nKey="contingentQuotaTableTitle">Quotas table for contingent</Trans>
                            <span className="contingent-name-title"> {contingent.name}</span>
                            <Link className="new-quota-button" to={`${prefix}/seating/quotas/inventory/${currentInventory.id()}/contingent/${contingent.pk}/edit`}>
                                <Trans i18nKey="newQuotaButton">+ New Quota</Trans>
                            </Link>
                        </>
                    }
                />
                {renderInventorySelector()}
                <p></p>
            </div>
            <Header as="h4">
                <div className="quota-actions">
                    <Link to={`${urlPrefix(eventId, organizationId)}/seating/quotas/inventory/${currentInventory.id()}`}>
                        <Trans i18nKey="quotasList">Quotas list</Trans>
                    </Link>
                </div>
                <span className="tabular-contingent-capacity">
                    <Trans i18nKey="tabularContingentCapacity">Total contingent capacity:</Trans>
                    <span> {contingent.booked} </span>
                    {contingent.bookingInProgress ? <Label> {contingent.bookingInProgress} </Label> : null}
                    <span> / {contingent.capacity}</span>
                </span>
            </Header>
            <table id="quotas-tabular">
                <thead>
                    <tr>
                        <Headers zones={zones} />
                    </tr>
                </thead>

                <tbody>{renderRows()}</tbody>
            </table>
        </React.Fragment>
    );
}

QuotasTabular.propTypes = {
    currentInventory: PropTypes.object.isRequired,
    rates: PropTypes.array.isRequired,
    renderInventorySelector: PropTypes.func.isRequired,
    zones: PropTypes.array.isRequired,
    contingent: PropTypes.object.isRequired,
    history: PropTypes.object.isRequired,
    quotas: PropTypes.object.isRequired
};
